<script setup lang="ts">
import { Info as InfoIcon } from '@lucide/vue'
import { ref, watch } from 'vue'

import BaseModal from '@/components/BaseModal.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseCheckbox from '@/components/ui/BaseCheckbox.vue'

const props = withDefaults(
  defineProps<{
    isOpen: boolean
    title?: string
    downloadLabel?: string
    isDownloading?: boolean
  }>(),
  {
    title: 'Download GPX',
    downloadLabel: 'Download GPX',
    isDownloading: false,
  },
)

const emit = defineEmits<{
  close: []
  download: [options: { includeRoutePoints: boolean }]
}>()

const includeRoutePoints = ref(false)
const showCompatibilityInfo = ref(false)

const resetState = () => {
  includeRoutePoints.value = false
  showCompatibilityInfo.value = false
}

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      resetState()
    }
  },
)

const handleClose = () => {
  if (props.isDownloading) return
  emit('close')
}

const handleDownload = () => {
  if (props.isDownloading) return
  emit('download', { includeRoutePoints: includeRoutePoints.value })
}
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    :title="title"
    description="Choose whether to include route points in the exported GPX file."
    size="md"
    show-footer
    :closeable="!isDownloading"
    :close-on-backdrop-click="!isDownloading"
    :close-on-escape="!isDownloading"
    @close="handleClose"
  >
    <div class="space-y-3" data-testid="gpx-download-options-modal">
      <div class="rounded-lg border border-gray-200 p-3">
        <div class="flex items-start gap-3">
          <BaseCheckbox
            id="include-route-points"
            v-model="includeRoutePoints"
            class="mt-0.5"
            data-testid="gpx-include-route-points-checkbox"
          />

          <div class="min-w-0 flex-1">
            <label
              for="include-route-points"
              class="block text-sm font-medium text-gray-900 hover:cursor-pointer"
            >
              Include Route Points
            </label>
            <p class="mt-1 text-xs text-gray-500">
              Adds GPX route data (`rte` / `rtept`) to the export.
            </p>
          </div>

          <button
            type="button"
            class="inline-flex h-7 w-7 items-center justify-center rounded-full text-primary-600 transition-colors hover:bg-primary-50 hover:text-primary-700 hover:cursor-pointer"
            aria-label="Route points compatibility info"
            data-testid="gpx-route-points-info-button"
            @click="showCompatibilityInfo = !showCompatibilityInfo"
          >
            <InfoIcon class="h-4 w-4" />
          </button>
        </div>

        <p
          v-if="showCompatibilityInfo"
          class="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800"
          data-testid="gpx-route-points-info-text"
        >
          Some bike computers, including some Wahoo devices, may stop showing track-point elevation
          correctly when route points are included. Leave this off unless you specifically need
          route points.
        </p>
      </div>
    </div>

    <template #footer>
      <BaseButton variant="secondary" :disabled="isDownloading" @click="handleClose"
        >Cancel</BaseButton
      >
      <BaseButton :loading="isDownloading" @click="handleDownload">{{ downloadLabel }}</BaseButton>
    </template>
  </BaseModal>
</template>
